import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { methodologyApi } from '../../api/methodology'
import { Correct } from '../../components/Correct'
import { PillarChip } from '../../components/PillarChip'
import { useSpoke } from '../../hooks/useSpoke'
import type { AskShapeResult, Tactic, TacticApplication, TacticResult } from '../../types/methodology'

// The Playbook: the firm's sales methodology as a workflow. The first methodology is Don's "The Activator
// Advantage", read from the PDF and laid out as tactics a rep runs on every pursuit — each with when to
// use it, how to do it, and how well it has converted (applied → booked / advanced / no response).

const CATEGORY_ORDER = ['mindset', 'prospecting', 'insight', 'connecting', 'proposing', 'negotiating', 'expanding']
const CATEGORY_WORDS: Record<string, string> = {
  mindset: 'Mindset', prospecting: 'Prospecting', insight: 'Insight', connecting: 'Connecting', proposing: 'Proposing', negotiating: 'Negotiating', expanding: 'Expanding',
}

export function Playbook() {
  const spoke = useSpoke()
  const list = useQuery({ queryKey: ['methodologies', spoke], queryFn: () => methodologyApi.list(spoke), staleTime: 30_000 })
  const [selected, setSelected] = useState<string | null>(null)
  const active = selected ?? list.data?.[0]?.methodologyId ?? null
  const detail = useQuery({ queryKey: ['methodology', spoke, active], queryFn: () => methodologyApi.get(spoke, active!), enabled: !!active, staleTime: 30_000 })

  const measureBy = useMemo(() => {
    const m = new Map<string, TacticResult>()
    for (const r of detail.data?.measure ?? []) m.set(r.tacticId, r)
    return m
  }, [detail.data])

  const grouped = useMemo(() => {
    const tactics = detail.data?.tactics ?? []
    const by = new Map<string, Tactic[]>()
    for (const t of [...tactics].sort((a, b) => a.sequence - b.sequence)) {
      const k = CATEGORY_ORDER.includes(t.category) ? t.category : 'mindset'
      if (!by.has(k)) by.set(k, [])
      by.get(k)!.push(t)
    }
    return [...by.entries()].sort((a, b) => CATEGORY_ORDER.indexOf(a[0]) - CATEGORY_ORDER.indexOf(b[0]))
  }, [detail.data])

  return (
    <section className="page playbook">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link> · the firm's methodology</p>
          <h1>Playbook</h1>
          <p className="lede">Your sales methodology as a workflow: the tactics to run on every pursuit, when to use each, and how well each one converts. The first is read from the book itself.</p>
        </div>
        {(list.data?.length ?? 0) > 1 && (
          <select value={active ?? ''} onChange={(e) => setSelected(e.target.value)}>
            {list.data!.map((m) => <option key={m.methodologyId} value={m.methodologyId}>{m.name}</option>)}
          </select>
        )}
      </header>

      {list.isPending && <p className="muted">Loading…</p>}
      {list.isSuccess && (list.data?.length ?? 0) === 0 && (
        <div className="card">
          <p className="muted">No methodology yet. An admin ingests one from a book or playbook PDF; the model lays out its tactics as a workflow. Don's <em>The Activator Advantage</em> is the intended first.</p>
        </div>
      )}

      {detail.isSuccess && detail.data && (
        <>
          <div className="card methodology-summary">
            <h2>{detail.data.methodology.name}</h2>
            <p className="muted small">from {detail.data.methodology.source}</p>
            <p>{detail.data.methodology.summary}</p>
          </div>

          <WhatsWorking measure={detail.data.measure} tactics={detail.data.tactics} byAskShape={detail.data.byAskShape ?? []} />

          <PlaysInFlight methodologyId={detail.data.methodology.methodologyId} tactics={detail.data.tactics} />

          {grouped.map(([category, tactics]) => (
            <div key={category} className="tactic-group">
              <h3 className="tactic-category">{CATEGORY_WORDS[category] ?? category}</h3>
              <ol className="tactics">
                {tactics.map((t) => <TacticCard key={t.tacticId} t={t} result={measureBy.get(t.tacticId)} />)}
              </ol>
            </div>
          ))}
        </>
      )}
    </section>
  )
}

function TacticCard({ t, result }: { t: Tactic; result?: TacticResult }) {
  const applied = result?.applied ?? 0
  const wins = (result?.booked ?? 0) + (result?.advanced ?? 0) + (result?.won ?? 0)
  const rate = applied > 0 ? Math.round((wins / applied) * 100) : null
  return (
    <li className="tactic">
      <div className="tactic-head">
        <strong>{t.name}</strong>
        {applied > 0 && <span className="tactic-metric" title="applied → booked/advanced/won">{applied} run{applied === 1 ? '' : 's'}{rate !== null ? ` · ${rate}% moved` : ''}</span>}
      </div>
      <p className="tactic-summary">{t.summary}</p>
      <dl className="tactic-facts">
        {t.trigger && <><dt>When</dt><dd>{t.trigger}</dd></>}
        {t.howTo && <><dt>How</dt><dd>{t.howTo}</dd></>}
        {t.signal && <><dt>Scout signal</dt><dd>{t.signal}</dd></>}
      </dl>
    </li>
  )
}

// The plays standing against live work: what is planned, what has been run, and what was put up and turned
// down. Every row carries the correction, because a play is a CHOICE Scout made from the playbook and a
// choice can be the wrong one — the person names the play that should have been picked and it is planned.
const STATUS_WORDS: Record<string, string> = { planned: 'planned', applied: 'run', skipped: 'skipped' }

function PlaysInFlight({ methodologyId, tactics }: { methodologyId: string; tactics: Tactic[] }) {
  const spoke = useSpoke()
  const apps = useQuery({
    queryKey: ['applications', spoke, methodologyId],
    queryFn: () => methodologyApi.applications(spoke, methodologyId),
    staleTime: 30_000,
  })
  const name = (id: string) => tactics.find((t) => t.tacticId === id)?.name ?? id
  const rows = (apps.data ?? []).filter((a: TacticApplication) => a.status !== 'skipped').slice(0, 25)
  if (apps.isPending || rows.length === 0) return null
  const first = rows[0]
  return (
    <div className="card plays-in-flight">
      <div className="card-head">
        <h3>Plays in flight</h3>
        <Correct kind="play" entityId={first.applicationId} entityLabel={name(first.tacticId)}
          currentValue={`tactic:${first.tacticId}`} label="Not the right play?" />
      </div>
      <ul className="plain plays-run">
        {rows.map((a) => (
          <li key={a.applicationId}>
            <strong>{name(a.tacticId)}</strong>
            <span className="muted small">{STATUS_WORDS[a.status] ?? a.status}{a.outcome ? ` · ${a.outcome}` : ''} · {a.appliedOn}</span>
            <Correct kind="play" entityId={a.applicationId} entityLabel={name(a.tacticId)} currentValue={`tactic:${a.tacticId}`} compact />
          </li>
        ))}
      </ul>
    </div>
  )
}

// Outcome learning (PM-004): what is actually working — the tactics run most and the ones that convert.
// A first read from the plays reps record on pursuits; it sharpens as more plays are logged, and gains
// per-sector and per-tier breakdowns once the firm's outcomes flow in.
const ASK_SHAPE_WORDS: Record<AskShapeResult['askShape'], string> = {
  'two-dates-plus-format': 'Two dates + a format', 'one-line-question': 'One-line question', 'owed-intro': 'The intro they owe', congratulation: 'Congratulation', none: 'No shape',
}

function WhatsWorking({ measure, tactics, byAskShape }: { measure: TacticResult[]; tactics: Tactic[]; byAskShape: AskShapeResult[] }) {
  const name = (id: string) => tactics.find((t) => t.tacticId === id)?.name ?? id
  const totalApplied = measure.reduce((n, m) => n + m.applied, 0)
  if (totalApplied === 0) {
    return <div className="card whats-working"><h3>What's working</h3><p className="muted">No plays recorded yet. Run a tactic from a pursuit (open a lead or opportunity) and mark how it landed; the conversion of each tactic shows here.</p></div>
  }
  const moved = (m: TacticResult) => m.booked + m.advanced + m.won
  const ranked = [...measure].filter((m) => m.applied > 0).sort((a, b) => (moved(b) / b.applied) - (moved(a) / a.applied)).slice(0, 5)
  const booked = measure.reduce((n, m) => n + m.booked, 0)
  const advanced = measure.reduce((n, m) => n + m.advanced, 0)
  return (
    <div className="card whats-working">
      <h3>What's working <PillarChip pillar={measure[0]?.pillar} /></h3>
      <div className="deal-totals">
        <div className="stat accent"><strong>{totalApplied}</strong><span className="muted">plays run</span></div>
        <div className="stat"><strong>{booked}</strong><span className="muted">meetings booked</span></div>
        <div className="stat"><strong>{advanced}</strong><span className="muted">pursuits advanced</span></div>
      </div>
      <p className="muted small">Best-converting tactics so far:</p>
      <ul className="plain">
        {ranked.map((m) => <li key={m.tacticId}><strong>{name(m.tacticId)}</strong><span className="muted small">{m.applied} run · {Math.round((moved(m) / m.applied) * 100)}% moved</span></li>)}
      </ul>
      {/* per tactic, in a rep's words: how many went out, how many got a reply, how many booked a meeting —
          measured nightly from the outbox (a reply from that person, a meeting with them after the send) */}
      <table className="tactic-measure">
        <thead><tr><th>Tactic</th><th>sent</th><th>replied</th><th>booked</th><th>won</th><th>no answer yet</th></tr></thead>
        <tbody>
          {[...measure].filter((m) => m.applied > 0).sort((a, b) => b.applied - a.applied).map((m) => (
            <tr key={m.tacticId}><td>{name(m.tacticId)}</td><td>{m.applied}</td><td>{m.advanced}</td><td>{m.booked}</td><td>{m.won}</td><td>{m.noResponse}</td></tr>
          ))}
        </tbody>
      </table>
      {/* and by the shape of the close (REQ-093, DEC-2026-09-23-10): which ask gets answered — two dated options
          and a format, or the one-line question after three silences */}
      {byAskShape.some((s) => s.sent > 0) && (
        <table className="tactic-measure ask-shape-measure">
          <thead><tr><th>Shape of the ask</th><th>sent</th><th>replied</th><th>booked</th><th>won</th><th>no answer yet</th></tr></thead>
          <tbody>
            {byAskShape.filter((s) => s.sent > 0).map((s) => (
              <tr key={s.askShape}><td>{ASK_SHAPE_WORDS[s.askShape] ?? s.askShape}</td><td>{s.sent}</td><td>{s.advanced}</td><td>{s.booked}</td><td>{s.won}</td><td>{s.noResponse}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
